import { call, put, select, takeLatest } from 'redux-saga/effects'
import { SubmitChartConstants } from '../store/submitChart.constants'
import { IFetchPRIDResponse } from '../store/submitChart.types'
import { getServiceIdSelector } from '../../user/selectors'
import { doSubmitChart, postSubmitChart } from '../store/submitChart.actions'
import { SubmitChartService } from '../services/submitChart.service'
import { IPcr } from '../../pcr/store/pcr.types'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { getPcrIdSelector } from '../../home/selectors'
import { handleApiError } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { setAuthenticationJwt } from '../../login/store/login.actions'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { setLockChart } from '../../completeChart/store/completeChart.actions'
import Logger from '../../../services/logger.service'
import { callAutoUploadDoSubmitChart, callAutoUploadPostSubmitChart } from '../../home/saga/autoUploadPCRs.saga'

export function* fetchPridSaga(pcrDoc: IPcr, isManualUpload = true) {
  try {
    const authenticationJwt: string = yield select(getAuthenticationJwtSelector)
    const submitChartService = new SubmitChartService(authenticationJwt)
    const serviceId: string = yield select(getServiceIdSelector)
    const pcrDocsService = new PcrDocsService(serviceId)
    const response: IFetchPRIDResponse = yield call([submitChartService, submitChartService.fetchPRID])
    const { data, status } = response

    if (status === 200 && data?.prid) {
      if (isManualUpload) {
        yield put(setAuthenticationJwt(response.authenticationJwt))
        const pcrDocUpdated = { ...pcrDoc, prid: data.prid.toString() }
        yield call([pcrDocsService, pcrDocsService.savePCR], pcrDocUpdated)
        yield put(doSubmitChart())
      } else {
        const updatedDoc = { ...pcrDoc, prid: data.prid.toString() }
        yield call(callAutoUploadDoSubmitChart, updatedDoc)
      }
    } else {
      yield* handlePridError(response, pcrDoc, isManualUpload)
    }
  } catch (e) {
    Logger.error(`Error inside fetchPridSaga - Reason: ${e}`)
  }
}

function* handlePridError(response: IFetchPRIDResponse, pcrDoc: IPcr, isManualUpload: boolean) {
  response.failure = true

  if (response.error) {
    response.responseMessage = response.error.message
    SubmitChartService.printAPIError(response.error)
  }
  if (response.messages?.length) {
    const msg = response.messages.find((x) => x.status === response.status)
    response.responseMessage = msg?.message
  } else {
    response.responseMessage =
      'An error occurred during fetching PRID. Please contact customer support for further assistance.'
  }

  const postSubmitChartResponse = SubmitChartService.createErrorResponse(
    `An error occurred during fetching PRID: ${response.responseMessage}`
  )

  if (isManualUpload) {
    yield put(setLockChart(0, false))
    yield put(postSubmitChart(postSubmitChartResponse))
    yield put(handleApiError(response))
  } else {
    yield call(callAutoUploadPostSubmitChart, pcrDoc, postSubmitChartResponse)
  }
}

export function* preSubmitChart() {
  try {
    const serviceId: string = yield select(getServiceIdSelector)
    const pcrDocsService = new PcrDocsService(serviceId)
    const pcrID: string = yield select(getPcrIdSelector)
    const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)

    const { prid } = pcrDoc
    if (!prid) {
      yield call(fetchPridSaga, pcrDoc)
    } else {
      yield put(doSubmitChart())
    }
  } catch (e: any) {
    const cause = e.message ? e.message : 'unknown'
    const response = SubmitChartService.createErrorResponse(`An error occurred during fetching PRID. Cause: ${cause}`)
    yield put(setLockChart(0, false))
    yield put(postSubmitChart(response))
    const errorResponse = {
      status: null,
      messages: [
        {
          status: null,
          display: [
            { id: 3, name: 'Alert' },
            { id: 1, name: 'Log' },
          ],
          message: `An error occurred during fetching PRID. Cause: ${e}`,
        },
      ],
    }
    yield put(handleApiError(errorResponse))
  }
}

export function* preSubmitChartSaga() {
  yield takeLatest([SubmitChartConstants.SET_PRE_SUBMIT_CHART], performWithMutexHOG, preSubmitChart)
}
